const COACH_DASHBOARD_TAB_ID = "CoachDashboard";
const COACH_LIBRARY_TAB_ID = "CoachLibrary";
const COACH_BILLING_TAB_ID = "CoachBilling";
const COACH_COMPETITIONS_TAB_ID = "CoachCompetitions";
const COACH_APPLICATIONS_TAB_ID = "CoachApplications";
const COACH_NUTRITION_TAB_ID = "CoachNutrition";
const COACH_SETTINGS_TAB_ID = "CoachSettings";

const ALLOWED_SIDEBAR_IDS = [
  COACH_DASHBOARD_TAB_ID,
  COACH_LIBRARY_TAB_ID,
  COACH_BILLING_TAB_ID,
  COACH_COMPETITIONS_TAB_ID,
  COACH_APPLICATIONS_TAB_ID,
  COACH_NUTRITION_TAB_ID,
  COACH_SETTINGS_TAB_ID,
];

const LEGACY_DEFAULT_MIDDLE_ITEMS = [
  { id: COACH_LIBRARY_TAB_ID, visible: true },
  { id: COACH_BILLING_TAB_ID, visible: true },
  { id: COACH_COMPETITIONS_TAB_ID, visible: true },
  { id: COACH_APPLICATIONS_TAB_ID, visible: true },
];

const CONFIGURABLE_MIDDLE_ITEM_IDS = [
  COACH_LIBRARY_TAB_ID,
  COACH_BILLING_TAB_ID,
  COACH_COMPETITIONS_TAB_ID,
  COACH_APPLICATIONS_TAB_ID,
  COACH_NUTRITION_TAB_ID,
];

const withNormalizedOrder = (items) =>
  items.map((item, index) => ({
    ...item,
    order: index,
  }));

const normalizeConfigItem = (item) => ({
  ...item,
  visible:
    item?.id === COACH_LIBRARY_TAB_ID ? true : item?.visible !== false,
});

const getAllowedSidebarIds = ({ allowNutrition = false } = {}) =>
  allowNutrition
    ? ALLOWED_SIDEBAR_IDS
    : ALLOWED_SIDEBAR_IDS.filter((id) => id !== COACH_NUTRITION_TAB_ID);

const getConfigurableMiddleItemIds = ({ allowNutrition = false } = {}) =>
  allowNutrition
    ? CONFIGURABLE_MIDDLE_ITEM_IDS
    : CONFIGURABLE_MIDDLE_ITEM_IDS.filter(
        (id) => id !== COACH_NUTRITION_TAB_ID,
      );

export const buildCoachActiveSidebarConfig = (
  sidebarConfig,
  { allowNutrition = false } = {},
) => {
  const allowedSidebarIds = getAllowedSidebarIds({ allowNutrition });
  const config = [{ id: COACH_DASHBOARD_TAB_ID, visible: true }];

  if (Array.isArray(sidebarConfig) && sidebarConfig.length > 0) {
    const middleItems = sidebarConfig.filter(
      (item) =>
        item?.id !== COACH_DASHBOARD_TAB_ID &&
        item?.id !== COACH_SETTINGS_TAB_ID &&
        allowedSidebarIds.includes(item?.id),
    );

    if (!middleItems.some((item) => item?.id === COACH_LIBRARY_TAB_ID)) {
      middleItems.unshift({ id: COACH_LIBRARY_TAB_ID, visible: true, order: 0 });
    }

    const sortedConfig = [...middleItems].sort(
      (left, right) => (left?.order ?? 0) - (right?.order ?? 0),
    );

    sortedConfig.forEach((item) => {
      config.push(normalizeConfigItem(item));
    });
  } else {
    config.push(...LEGACY_DEFAULT_MIDDLE_ITEMS);
  }

  config.push({ id: COACH_SETTINGS_TAB_ID, visible: true });

  return withNormalizedOrder(config);
};

export const buildCoachEditableSidebarConfig = (
  sidebarConfig,
  { allowNutrition = false } = {},
) => {
  const configurableMiddleItemIds = getConfigurableMiddleItemIds({
    allowNutrition,
  });
  const activeConfig = buildCoachActiveSidebarConfig(sidebarConfig, {
    allowNutrition,
  });
  const middleItems = activeConfig
    .filter(
      (item) =>
        item.id !== COACH_DASHBOARD_TAB_ID && item.id !== COACH_SETTINGS_TAB_ID,
    )
    .map((item) => normalizeConfigItem(item));

  const presentIds = new Set(middleItems.map((item) => item.id));
  const missingItems = configurableMiddleItemIds.filter(
    (itemId) => !presentIds.has(itemId),
  ).map((itemId) => ({
    id: itemId,
    visible: itemId === COACH_LIBRARY_TAB_ID,
  }));

  return withNormalizedOrder([
    { id: COACH_DASHBOARD_TAB_ID, visible: true },
    ...middleItems,
    ...missingItems,
    { id: COACH_SETTINGS_TAB_ID, visible: true },
  ]);
};

export const buildCoachInitialSidebarConfig = (selectedFeatures = []) => {
  const selectedFeatureSet = new Set(
    Array.isArray(selectedFeatures) ? selectedFeatures : [],
  );

  return withNormalizedOrder([
    { id: COACH_LIBRARY_TAB_ID, visible: true },
    {
      id: COACH_BILLING_TAB_ID,
      visible: selectedFeatureSet.has("billing"),
    },
    {
      id: COACH_COMPETITIONS_TAB_ID,
      visible: selectedFeatureSet.has("competitions"),
    },
    {
      id: COACH_APPLICATIONS_TAB_ID,
      visible: false,
    },
    {
      id: COACH_NUTRITION_TAB_ID,
      visible: selectedFeatureSet.has("nutrition"),
    },
  ]);
};

export {
  COACH_APPLICATIONS_TAB_ID,
  COACH_DASHBOARD_TAB_ID,
  COACH_NUTRITION_TAB_ID,
  COACH_SETTINGS_TAB_ID,
};
